/**
 * SourceGenerator Edit Page
 * Edit existing source generator
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { sourceGeneratorService } from '../../services/sourceGeneratorService';
import { SourceGenerator, SourceGeneratorFormData } from '../../types/sourceGenerator';
import { SourceGeneratorForm } from './components/SourceGeneratorForm';

export const SourceGeneratorEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [sourceGenerator, setSourceGenerator] = useState<SourceGenerator | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load source generator
        if (id) {
          console.log('[EditPage] Loading source generator:', id);
          const sg = await sourceGeneratorService.getSourceGeneratorById(id);
          console.log('[EditPage] Loaded source generator:', sg);
          setSourceGenerator(sg);
        }
      } catch (err: unknown) {
        setFalha(
          falhaDeCarga(err, 'Não foi possível carregar a fonte geradora. Tente de novo.'),
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (data: SourceGeneratorFormData) => {
    if (!id) return;

    try {
      setSaving(true);
      setError(null);

      console.log('[EditPage] Updating source generator:', id, data);

      await sourceGeneratorService.updateSourceGenerator(id, data);

      console.log('[EditPage] Source generator updated successfully');

      navigate('/source-generators');
    } catch (err: unknown) {
      const error = err as Error;
      console.error('[EditPage] Error updating source generator:', error);
      setError(error.message ?? 'Erro ao atualizar fonte geradora');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/source-generators');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Fontes Geradoras', path: '/source-generators' },
    { label: 'Editar' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Fonte Geradora">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!sourceGenerator) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Fonte Geradora">
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Fonte geradora não encontrada.')}
          rotuloDoBotao="Voltar para fontes geradoras"
          aoVoltar={() => navigate('/source-generators')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Fonte Geradora"
      subtitle={sourceGenerator.name}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <SourceGeneratorForm
            initialData={sourceGenerator}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={saving}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default SourceGeneratorEditPage;
